/**
 * The "what arrives in the box" rows.
 *
 * Facts about the object, in the store's own voice — deliberately **not** price, stock or
 * shipping. Commerce lives outside Firebase, and the store renders those from the commerce
 * provider; a stock count in the catalog would be exactly the fake-scarcity the product does
 * not have a component for.
 *
 * `studio/06` taught the other half of this: a row still carrying a `[ … ]` placeholder is
 * dropped by the Studio's picker rather than shown with its brackets, so an unfinished row
 * here is invisible there. Finishing it is the fix, which is why this editor exists at all.
 */

import { Button, TextLink } from '@atlas-nook/design-system';
import type { Localized } from '@atlas-nook/mediums';
import type { JSX } from 'react';

import { ADMIN_STRINGS as S } from '../strings.js';
import { LocalizedField } from './LocalizedField.js';

export function WhatArrivesEditor({
  rows,
  onChange,
}: {
  rows: Localized[] | undefined;
  onChange: (next: Localized[] | undefined) => void;
}): JSX.Element {
  const list = rows ?? [];

  function replace(index: number, value: Localized | undefined): void {
    const next = [...list];
    if (value === undefined) next.splice(index, 1);
    else next[index] = value;
    onChange(next.length === 0 ? undefined : next);
  }

  return (
    <div className="stack">
      <p className="note">{S.medium.arrivesHelp}</p>

      {list.map((row, index) => (
        <div key={index} className="arrow">
          <LocalizedField
            label={`${index + 1}`}
            value={row}
            required
            onChange={(value) => replace(index, value)}
          />
          <TextLink onClick={() => replace(index, undefined)}>
            {S.medium.arrivesRemove(index + 1)}
          </TextLink>
        </div>
      ))}

      <div className="btnrow">
        <Button tone="quiet" size="sm" onClick={() => onChange([...list, { en: '' }])}>
          {S.medium.arrivesAdd}
        </Button>
      </div>
    </div>
  );
}
